import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { MANUAL_BALANCE_ADJUSTMENT } from "../graphql/wallet-history";
import { BalanceChangeType } from "../types/wallet-history";

interface ManualBalanceAdjustmentProps {
  open: boolean;
  onClose: () => void;
  walletId: string;
  onSuccess?: () => void;
}

export const ManualBalanceAdjustment: React.FC<
  ManualBalanceAdjustmentProps
> = ({ open, onClose, walletId, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [changeType, setChangeType] = useState<BalanceChangeType>(
    BalanceChangeType.MANUAL_ADJUSTMENT,
  );
  const [reason, setReason] = useState("");
  const [mutationError, setMutationError] = useState<Error | null>(null);

  const [manualBalanceAdjustment, { loading, error, data: manualData }] =
    useMutation(MANUAL_BALANCE_ADJUSTMENT);

  // Handle mutation side-effects via useEffect instead of onCompleted/onError
  React.useEffect(() => {
    if (manualData) {
      setAmount("");
      setChangeType(BalanceChangeType.MANUAL_ADJUSTMENT);
      setReason("");
      setMutationError(null);
      onClose();
      onSuccess?.();
    }
  }, [manualData, onClose, onSuccess]);

  React.useEffect(() => {
    if (error) {
      setMutationError(error);
    }
  }, [error]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) === 0) {
      return;
    }

    setMutationError(null);
    manualBalanceAdjustment({
      variables: {
        input: {
          walletId,
          amount: parseFloat(amount),
          changeType,
          reason: reason || undefined,
        },
      },
    }).catch(() => {}); // Prevent unhandled rejection
  };

  const handleClose = () => {
    setAmount("");
    setChangeType(BalanceChangeType.MANUAL_ADJUSTMENT);
    setReason("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manual Balance Adjustment</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {mutationError ? (
              <Alert severity="error">Error: {mutationError.message}</Alert>
            ) : error ? (
              <Alert severity="error">Error: {error.message}</Alert>
            ) : null}

            <FormControl fullWidth>
              <InputLabel>Adjustment Type</InputLabel>
              <Select
                value={changeType}
                onChange={(e) =>
                  setChangeType(e.target.value as BalanceChangeType)
                }
                label="Adjustment Type"
              >
                <MenuItem value={BalanceChangeType.DEPOSIT}>Deposit</MenuItem>
                <MenuItem value={BalanceChangeType.WITHDRAWAL}>
                  Withdrawal
                </MenuItem>
                <MenuItem value={BalanceChangeType.MANUAL_ADJUSTMENT}>
                  Manual Adjustment
                </MenuItem>
                <MenuItem value={BalanceChangeType.FEE}>Fee</MenuItem>
                <MenuItem value={BalanceChangeType.INTEREST}>Interest</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              required
              inputProps={{ "data-testid": "amount-input", step: "0.01" }}
              helperText={
                changeType === BalanceChangeType.DEPOSIT ||
                changeType === BalanceChangeType.INTEREST
                  ? "Positive amount for deposit/interest"
                  : changeType === BalanceChangeType.WITHDRAWAL ||
                      changeType === BalanceChangeType.FEE
                    ? "Positive amount for withdrawal/fee (will be subtracted)"
                    : "Positive or negative amount for manual adjustment"
              }
            />

            <TextField
              label="Reason (Optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter reason for this adjustment..."
            />

            <Typography variant="body2" color="text.secondary">
              This will create a balance history entry and activity record for
              the wallet.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !amount || parseFloat(amount) === 0}
          >
            {loading ? "Processing..." : "Apply Adjustment"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
